import axios from "axios";
import { LOGIN } from "@/router/routeNames";
import router from "@/router";
import jwt_decode from "jwt-decode";
import Tools from "@/utils/tools";
import { clearUserSession } from "@/utils/authentHelpers";

/* 
  use sessionStorage instead of localStorage
  localStorage store without time limit,
  sessionStorage store until the session (browser, tab) is closed
*/

async function callAuth(data, store) {
  let host = window.JS_APP_AUTH_URL;

  const response = await axios.post(`${host}/connect/token`, data, {
    headers: {
      "content-type": "application/x-www-form-urlencoded",
    },
    auth: {
      username: process.env.VUE_APP_AUTH_CLIENTID,
      password: process.env.VUE_APP_AUTH_CLIENTSECRET,
    },
  });

  store.commit("setAccessToken", response.data.access_token);
  store.commit("setRefreshToken", response.data.refresh_token);
}

function setUserData(state, accessToken) {
  if (accessToken == null) {
    state.expiration = null;
    state.userData = null;
    state.userRights = null;
  } else {
    const decodedJwt = jwt_decode(accessToken);
    state.expiration = new Date(decodedJwt.exp * 1000);
    state.userData = decodedJwt;
    state.userRights = decodedJwt.role_rights
      ? JSON.parse(decodedJwt.role_rights)
      : [];
  }
}

export default {
  namespaced: true,
  state: {
    accessToken: null,
    expiration: new Date(),
    refreshToken: null,
    // store userData
    userData: null,
    userRights: null,
  },
  mutations: {
    init(state) {
      state.accessToken = sessionStorage.getItem("auth.accessToken");
      state.refreshToken = sessionStorage.getItem("auth.refreshToken");
      setUserData(state, state.accessToken);
    },

    initFromUri(state, payload) {
      state.accessToken = payload;
      setUserData(state, state.accessToken);
    },

    setAccessToken(state, data) {
      state.accessToken = data;
      setUserData(state, state.accessToken);
      Tools.StorageHelper.setStorageItem(
        sessionStorage,
        "auth.accessToken",
        data
      );
    },
    setRefreshToken(state, data) {
      state.refreshToken = data;
      Tools.StorageHelper.setStorageItem(
        sessionStorage,
        "auth.refreshToken",
        data
      );
    },
  },
  actions: {
    signIn(store, { email, password }) {
      if (store.state.accessToken) {
        return;
      }

      const data = new URLSearchParams();
      data.append("grant_type", "password");
      data.append("scope", "API offline_access profile");
      data.append("username", email);
      data.append("password", password);

      return callAuth(data, store);
    },

    async ensureToken(store) {
      if (!store.getters.getRefreshToken) {
        throw new Error("no_refresh_token");
      }

      const data = new URLSearchParams();
      data.append("grant_type", "refresh_token");
      data.append("scope", "API offline_access");
      data.append("refresh_token", store.state.refreshToken);

      await callAuth(data, store);
    },

    logout({ commit }) {
      clearUserSession();
      commit("setAccessToken", null);
      commit("setRefreshToken", null);

      // catch to prevent router push's warnings to be displayed in the console
      router.push({ name: LOGIN }).catch(() => { });
    },
  },
  getters: {
    isAuthenticated: (state) => {
      return state.accessToken != null && state.refreshToken != null;
    },
    isExpired: (state, getters) => {
      return state.expiration > new Date() && getters.isAuthenticated;
    },
    isAdminMba: (state) => {
      return state.userData.role === 'AdminMba';
    },

    getAccessToken: (state) => state.accessToken,

    getRefreshToken: (state) => state.refreshToken,

    getEmail: ({ userData }) => (userData ? userData.email : ""),

    getName: ({ userData }) => (userData ? userData.name : ""),

    getRoleRank: ({ userData }) => parseInt(userData.role_rank),

    getRole: (state) => state.userData.role,

    getUserRoleId: (state) => state.userData.role_id,

    hasRight({ userRights }) {
      return (requiredRight) =>
        userRights.some((right) => {
          if (!requiredRight) {
            return;
          }

          const formattedRight = right.trim().toLowerCase();
          const formattedRequiredRight = requiredRight.trim().toLowerCase();

          return formattedRight === formattedRequiredRight;
        });
    },
  },
};
